import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { getApiBase } from '../utils/apiBase';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Store {
  _id: string;
  name: string;
  description?: string;
}

interface Product {
  _id: string;
  name: string;
  barcode?: string;
  price: number;
  category?: string;
}

type DiscountType = 'percentage' | 'flat' | 'bundle';
type PromoStatus = 'active' | 'scheduled' | 'expired' | 'inactive';

interface Promotion {
  _id: string;
  storeId: string;
  name: string;
  description?: string;
  productIds: Product[];
  discountType: DiscountType;
  discountValue: number;
  bundleQty?: number | null;
  bundlePrice?: number | null;
  startDate: string;
  endDate: string;
  isActive: boolean;
  computedStatus: PromoStatus;
  createdAt: string;
}

interface FormState {
  name: string;
  description: string;
  productIds: string[];
  discountType: DiscountType;
  discountValue: string;
  bundleQty: string;
  bundlePrice: string;
  startDate: string;
  endDate: string;
  isActive: boolean;
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const s = {
  page: {
    padding: '32px',
    background: '#f8f7f5',
    minHeight: '100vh',
    fontFamily: "'DM Sans', sans-serif",
  } as React.CSSProperties,
  inp: {
    width: '100%',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '9px 12px',
    fontSize: '14px',
    color: '#111827',
    outline: 'none',
    boxSizing: 'border-box' as const,
    fontFamily: "'DM Sans', sans-serif",
    background: '#fff',
  },
  label: {
    fontSize: '11px',
    color: '#9ca3af',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.06em',
    marginBottom: '4px',
    display: 'block',
    fontWeight: 600,
  },
  overlay: {
    position: 'fixed' as const,
    inset: 0,
    background: 'rgba(0,0,0,0.45)',
    zIndex: 50,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px',
  },
  modal: {
    background: '#fff',
    borderRadius: '16px',
    width: '100%',
    maxWidth: '540px',
    maxHeight: '90vh',
    overflowY: 'auto' as const,
  },
  cancelBtn: {
    padding: '9px 18px',
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
    background: '#fff',
    fontSize: '14px',
    fontWeight: 500,
    color: '#374151',
    cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif",
  },
  saveBtn: {
    padding: '9px 18px',
    borderRadius: '8px',
    border: 'none',
    background: '#F1561D',
    color: '#fff',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif",
  },
};


// ─── Small helpers ────────────────────────────────────────────────────────────

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div><span style={s.label}>{label}</span>{children}</div>
);

function statusColor(status: PromoStatus): { bg: string; text: string; dot: string } {
  const map: Record<PromoStatus, { bg: string; text: string; dot: string }> = {
    active:    { bg: '#f0fdf4', text: '#16a34a', dot: '#16a34a' },
    scheduled: { bg: '#eff6ff', text: '#2563eb', dot: '#2563eb' },
    expired:   { bg: '#f9fafb', text: '#9ca3af', dot: '#9ca3af' },
    inactive:  { bg: '#fef2f2', text: '#dc2626', dot: '#dc2626' },
  };
  return map[status] || map.inactive;
}

function formatDiscount(p: Promotion): string {
  if (p.discountType === 'percentage') return `${p.discountValue}% off`;
  if (p.discountType === 'flat') return `R${p.discountValue} off`;
  if (p.discountType === 'bundle') return `${p.bundleQty} for R${p.bundlePrice}`;
  return '—';
}

function formatDate(d: string): string {
  return new Date(d).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' });
}

function toDatetimeLocal(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function defaultForm(): FormState {
  const now = new Date();
  const end = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
  const toLocal = (d: Date) => {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };
  return {
    name: '',
    description: '',
    productIds: [],
    discountType: 'percentage',
    discountValue: '',
    bundleQty: '',
    bundlePrice: '',
    startDate: toLocal(now),
    endDate: toLocal(end),
    isActive: true,
  };
}

// ─── SVG Icons (inline — no emoji, no external libs beyond lucide if you import it) ─

const IconTag = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>
    <line x1="7" y1="7" x2="7.01" y2="7"/>
  </svg>
);

const IconPlus = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);

const IconX = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

const IconEdit = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);

const IconTrash = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
    <path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
  </svg>
);

const IconToggle = ({ on }: { on: boolean }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {on
      ? <><rect x="1" y="5" width="22" height="14" rx="7" ry="7"/><circle cx="16" cy="12" r="3" fill="currentColor"/></>
      : <><rect x="1" y="5" width="22" height="14" rx="7" ry="7"/><circle cx="8" cy="12" r="3" fill="currentColor"/></>
    }
  </svg>
);

const IconPackage = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.3 }}>
    <path d="M16.5 9.4L7.55 4.24"/><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
    <polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/>
  </svg>
);

const IconStore = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#F1561D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
    <polyline points="9 22 9 12 15 12 15 22"/>
  </svg>
);

const IconChevronLeft = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6"/>
  </svg>
);

// ─── Product Multi-Select ─────────────────────────────────────────────────────

const ProductSelector: React.FC<{
  products: Product[];
  selected: string[];
  onChange: (ids: string[]) => void;
}> = ({ products, selected, onChange }) => {
  const [search, setSearch] = useState('');

  // 👇 Add these two lines here
  console.log('selected raw:', selected);
  console.log('first product _id:', products[0]?._id, typeof products[0]?._id);
  console.log('all product _ids:', products.map(p => p._id));

  // Normalize selected IDs once
  const normalizedSelected = selected.map(id =>
    typeof id === 'object' && id !== null ? String((id as any)._id ?? id) : String(id)
  );

  const filtered = products.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    (p.barcode || '').includes(search)
  );

  const toggle = (id: string) => {
    const sid = String(id);
    if (normalizedSelected.includes(sid)) {
      onChange(normalizedSelected.filter(s => s !== sid));
    } else {
      onChange([...normalizedSelected, sid]);
    }
  };

  return (
    <div>
      <input
        style={{ ...s.inp, marginBottom: '8px' }}
        placeholder="Search products... (leave empty for store-wide)"
        value={search}
        onChange={e => setSearch(e.target.value)}
      />
      {normalizedSelected.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '8px' }}>
          {normalizedSelected.map(id => {
            const p = products.find(x => String(x._id) === id);
            return p ? (
              <span key={id} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', background: '#fff5f2', color: '#F1561D', border: '1px solid #fecdd3', borderRadius: '20px', padding: '3px 10px', fontWeight: 500 }}>
                {p.name}
                <button onClick={() => toggle(id)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#F1561D', display: 'flex', alignItems: 'center' }}>
                  <IconX />
                </button>
              </span>
            ) : null;
          })}
        </div>
      )}
      <div style={{ maxHeight: '160px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
        {filtered.length === 0 ? (
          <div style={{ padding: '12px', fontSize: '13px', color: '#9ca3af', textAlign: 'center' }}>No products found</div>
        ) : (
          filtered.map(p => {
            const checked = normalizedSelected.includes(String(p._id));
            return (
              <label key={p._id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6', background: checked ? '#fff5f2' : 'transparent', transition: 'background 0.1s' }}>
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(String(p._id))}
                  style={{ accentColor: '#F1561D', width: '15px', height: '15px', cursor: 'pointer', flexShrink: 0 }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ margin: 0, fontSize: '13px', fontWeight: 500, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</p>
                  <p style={{ margin: '1px 0 0', fontSize: '11px', color: '#9ca3af', fontFamily: 'monospace' }}>{p.barcode || 'No barcode'} · R{p.price?.toFixed(2)}</p>
                </div>
              </label>
            );
          })
        )}
      </div>
      <p style={{ margin: '6px 0 0', fontSize: '11px', color: '#9ca3af' }}>
        {normalizedSelected.length === 0
          ? 'No products selected — promotion will apply to the entire store'
          : `${normalizedSelected.length} product${normalizedSelected.length !== 1 ? 's' : ''} selected`}
      </p>
    </div>
  );
};

// ─── Promotion Form Modal ─────────────────────────────────────────────────────

const PromotionModal: React.FC<{
  title: string;
  form: FormState;
  setForm: React.Dispatch<React.SetStateAction<FormState>>;
  products: Product[];
  onSubmit: (e: React.FormEvent) => void;
  onClose: () => void;
  saving: boolean;
  submitLabel: string;
}> = ({ title, form, setForm, products, onSubmit, onClose, saving, submitLabel }) => {
  const set = (key: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm(f => ({ ...f, [key]: e.target.value }));

  return (
    <div style={s.overlay} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={s.modal}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px 0' }}>
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#111827' }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', display: 'flex', alignItems: 'center' }}>
            <IconX />
          </button>
        </div>

        <form onSubmit={onSubmit}>
          <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: '14px' }}>

            {/* Name */}
            <Field label="Promotion Name">
              <input style={s.inp} placeholder="e.g. Weekend Sale" value={form.name} onChange={set('name')} required />
            </Field>

            {/* Description */}
            <Field label="Description (optional)">
              <textarea
                style={{ ...s.inp, resize: 'vertical', minHeight: '64px' }}
                placeholder="Brief description shown to customers"
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              />
            </Field>

            {/* Discount type */}
            <Field label="Discount Type">
              <select
                style={{ ...s.inp, cursor: 'pointer' }}
                value={form.discountType}
                onChange={e => setForm(f => ({ ...f, discountType: e.target.value as DiscountType, discountValue: '', bundleQty: '', bundlePrice: '' }))}
              >
                <option value="percentage">Percentage off (e.g. 10%)</option>
                <option value="flat">Flat price reduction (e.g. R5 off)</option>
                <option value="bundle">Bundle deal (e.g. 2 for R50)</option>
              </select>
            </Field>

            {/* Discount value fields */}
            {form.discountType === 'percentage' && (
              <Field label="Discount Percentage (%)">
                <input style={s.inp} type="number" min="0" max="100" step="0.01" placeholder="e.g. 15" value={form.discountValue} onChange={set('discountValue')} required />
              </Field>
            )}

            {form.discountType === 'flat' && (
              <Field label="Amount Off (R)">
                <input style={s.inp} type="number" min="0" step="0.01" placeholder="e.g. 5.00" value={form.discountValue} onChange={set('discountValue')} required />
              </Field>
            )}

            {form.discountType === 'bundle' && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                <Field label="Buy Quantity">
                  <input style={s.inp} type="number" min="2" step="1" placeholder="e.g. 2" value={form.bundleQty} onChange={set('bundleQty')} required />
                </Field>
                <Field label="Total Bundle Price (R)">
                  <input style={s.inp} type="number" min="0" step="0.01" placeholder="e.g. 50.00" value={form.bundlePrice} onChange={set('bundlePrice')} required />
                </Field>
              </div>
            )}

            {/* Date range */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              <Field label="Start Date & Time">
                <input style={s.inp} type="datetime-local" value={form.startDate} onChange={set('startDate')} required />
              </Field>
              <Field label="End Date & Time">
                <input style={s.inp} type="datetime-local" value={form.endDate} onChange={set('endDate')} required />
              </Field>
            </div>

            {/* Products */}
            <Field label="Apply To Products">
              <ProductSelector
                products={products}
                selected={form.productIds}
                onChange={ids => setForm(f => ({ ...f, productIds: ids }))}
              />
            </Field>

            {/* Active toggle */}
            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', userSelect: 'none' }}>
              <div
                onClick={() => setForm(f => ({ ...f, isActive: !f.isActive }))}
                style={{
                  width: '44px', height: '24px', borderRadius: '12px',
                  background: form.isActive ? '#F1561D' : '#e5e7eb',
                  position: 'relative', transition: 'background 0.2s', cursor: 'pointer', flexShrink: 0,
                }}
              >
                <div style={{
                  position: 'absolute', top: '2px',
                  left: form.isActive ? '22px' : '2px',
                  width: '20px', height: '20px',
                  borderRadius: '50%', background: '#fff',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                  transition: 'left 0.2s',
                }} />
              </div>
              <span style={{ fontSize: '14px', color: '#374151', fontWeight: 500 }}>
                {form.isActive ? 'Active (promotion will run based on dates)' : 'Inactive (paused)'}
              </span>
            </label>
          </div>

          {/* Footer */}
          <div style={{ padding: '0 24px 20px', display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <button type="button" style={s.cancelBtn} onClick={onClose} disabled={saving}>Cancel</button>
            <button
              type="submit"
              disabled={saving}
              style={{ ...s.saveBtn, opacity: saving ? 0.7 : 1, cursor: saving ? 'not-allowed' : 'pointer', minWidth: '130px' }}
            >
              {saving ? 'Saving…' : submitLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

interface PromotionsProps {
  searchTerm?: string;
}

// Add this component above the Promotions component
const StoreLogo: React.FC<{ logo?: string | null; name: string }> = ({ logo, name }) => {
  const [errored, setErrored] = useState(false);
  useEffect(() => { setErrored(false); }, [logo]);

  if (logo && !errored) {
    return (
      <img
        src={logo}
        alt={`${name} logo`}
        onError={() => setErrored(true)}
        style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '12px' }}
      />
    );
  }
  return <IconStore />;
};

const Promotions: React.FC<PromotionsProps> = ({ searchTerm = '' }) => {
  const [stores, setStores] = useState<Store[]>([]);
  const [selectedStore, setSelectedStore] = useState<Store | null>(null);
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loadingStores, setLoadingStores] = useState(true);
  const [loadingPromos, setLoadingPromos] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingPromo, setEditingPromo] = useState<Promotion | null>(null);
  const [form, setForm] = useState<FormState>(defaultForm());
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [currentUser, setCurrentUser] = useState<any>(null);

  const API_BASE = getApiBase();
  const token = localStorage.getItem('authToken');
  const authHeader = { Authorization: `Bearer ${token}` };

  // ── Fetch current user ─────────────────────────────────────────────────────
  useEffect(() => {
    axios.get(`${API_BASE}/api/auth/me`, { headers: authHeader })
      .then(r => setCurrentUser(r.data.user ?? r.data))
      .catch(() => {});
  }, []);

  // ── Fetch stores ───────────────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      try {
        setLoadingStores(true);
        const r = await axios.get(`${API_BASE}/api/stores`, { headers: authHeader });
        const arr: Store[] = Array.isArray(r.data.data) ? r.data.data : [];
        if (currentUser?.role === 'manager' && currentUser?.storeId) {
          const ms = arr.find(s => s._id === currentUser.storeId);
          if (ms) { setStores([ms]); setSelectedStore(ms); }
        } else {
          setStores(arr);
        }
      } catch { setStores([]); }
      finally { setLoadingStores(false); }
    };
    load();
  }, [currentUser]);

  // ── Fetch promotions + products for selected store ─────────────────────────
  const fetchData = useCallback(async () => {
    if (!selectedStore) return;
    try {
      setLoadingPromos(true);
      const [promoRes, prodRes] = await Promise.all([
        axios.get(`${API_BASE}/api/promotions?storeId=${selectedStore._id}`, { headers: authHeader }),
        axios.get(`${API_BASE}/api/products?storeId=${selectedStore._id}`, { headers: authHeader }),
      ]);
      setPromotions(Array.isArray(promoRes.data.data) ? promoRes.data.data : []);
      setProducts(Array.isArray(prodRes.data.data) ? prodRes.data.data : []);
    } catch { setPromotions([]); setProducts([]); }
    finally { setLoadingPromos(false); }
  }, [selectedStore, API_BASE]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Create ─────────────────────────────────────────────────────────────────
  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedStore) return;
    setSaving(true);
    try {
      await axios.post(`${API_BASE}/api/promotions`, {
        storeId: selectedStore._id,
        name: form.name,
        description: form.description,
        productIds: form.productIds,
        discountType: form.discountType,
        discountValue: form.discountType !== 'bundle' ? Number(form.discountValue) : 0,
        bundleQty: form.discountType === 'bundle' ? Number(form.bundleQty) : null,
        bundlePrice: form.discountType === 'bundle' ? Number(form.bundlePrice) : null,
        startDate: new Date(form.startDate).toISOString(),
        endDate: new Date(form.endDate).toISOString(),
        isActive: form.isActive,
      }, { headers: authHeader });
      setShowCreateModal(false);
      setForm(defaultForm());
      fetchData();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to create promotion');
    } finally { setSaving(false); }
  };

  // ── Edit ───────────────────────────────────────────────────────────────────
 const openEdit = (promo: Promotion) => {
  setForm({
    name: promo.name,
    description: promo.description || '',
    productIds: (promo.productIds || []).map((p: any) =>
      typeof p === 'object' && p !== null ? String(p._id) : String(p)
    ),
    discountType: promo.discountType,
    discountValue: promo.discountType !== 'bundle' ? String(promo.discountValue) : '',
    bundleQty: promo.bundleQty != null ? String(promo.bundleQty) : '',
    bundlePrice: promo.bundlePrice != null ? String(promo.bundlePrice) : '',
    startDate: toDatetimeLocal(promo.startDate),
    endDate: toDatetimeLocal(promo.endDate),
    isActive: promo.isActive,
  });
  setEditingPromo(promo);
  setShowEditModal(true);
};

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingPromo) return;
    setSaving(true);
    try {
      await axios.put(`${API_BASE}/api/promotions/${editingPromo._id}`, {
        name: form.name,
        description: form.description,
        productIds: form.productIds,
        discountType: form.discountType,
        discountValue: form.discountType !== 'bundle' ? Number(form.discountValue) : 0,
        bundleQty: form.discountType === 'bundle' ? Number(form.bundleQty) : null,
        bundlePrice: form.discountType === 'bundle' ? Number(form.bundlePrice) : null,
        startDate: new Date(form.startDate).toISOString(),
        endDate: new Date(form.endDate).toISOString(),
        isActive: form.isActive,
      }, { headers: authHeader });
      setShowEditModal(false);
      setEditingPromo(null);
      setForm(defaultForm());
      fetchData();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to update promotion');
    } finally { setSaving(false); }
  };

  // ── Toggle ─────────────────────────────────────────────────────────────────
  const handleToggle = async (id: string) => {
    setToggling(id);
    try {
      await axios.patch(`${API_BASE}/api/promotions/${id}/toggle`, {}, { headers: authHeader });
      fetchData();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to toggle promotion');
    } finally { setToggling(null); }
  };

  // ── Delete ─────────────────────────────────────────────────────────────────
  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this promotion? This cannot be undone.')) return;
    setDeleting(id);
    try {
      await axios.delete(`${API_BASE}/api/promotions/${id}`, { headers: authHeader });
      fetchData();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to delete promotion');
    } finally { setDeleting(null); }
  };

  // ── Derived ────────────────────────────────────────────────────────────────
  const filtered = promotions.filter(p => {
    const matchSearch = p.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatus = statusFilter === 'all' || p.computedStatus === statusFilter;
    return matchSearch && matchStatus;
  });

  const counts = {
    all: promotions.length,
    active: promotions.filter(p => p.computedStatus === 'active').length,
    scheduled: promotions.filter(p => p.computedStatus === 'scheduled').length,
    expired: promotions.filter(p => p.computedStatus === 'expired').length,
    inactive: promotions.filter(p => p.computedStatus === 'inactive').length,
  };

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loadingStores) {
    return <div style={{ padding: '32px', fontFamily: 'DM Sans, sans-serif', color: '#9ca3af' }}>Loading stores…</div>;
  }

  // ── Store picker ───────────────────────────────────────────────────────────
  if (!selectedStore) {
    return (
      <div style={s.page}>
        <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600&display=swap');`}</style>
        <div style={{ marginBottom: '28px' }}>
          <h1 style={{ margin: 0, fontSize: '28px', fontWeight: 600, color: '#111827' }}>Promotions</h1>
          <p style={{ margin: '4px 0 0', fontSize: '14px', color: '#9ca3af' }}>Select a store to manage its promotions</p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '16px' }}>
          {stores.map(store => (
            <div
              key={store._id}
              onClick={() => setSelectedStore(store)}
              style={{ background: '#fff', borderRadius: '14px', border: '1px solid #f3f4f6', padding: '24px', cursor: 'pointer', transition: 'border-color 0.15s, box-shadow 0.15s' }}
              onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.borderColor = '#F1561D'; (e.currentTarget as HTMLDivElement).style.boxShadow = '0 4px 20px rgba(241,86,29,0.1)'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.borderColor = '#f3f4f6'; (e.currentTarget as HTMLDivElement).style.boxShadow = 'none'; }}
            >
              {/* ── Logo or fallback ── */}
              <div style={{
                width: '44px', height: '44px', borderRadius: '12px',
                background: (store as any).logo ? 'transparent' : '#fff5f2',
                border: (store as any).logo ? '1px solid #f3f4f6' : 'none',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                marginBottom: '14px', overflow: 'hidden',
              }}>
                <StoreLogo logo={(store as any).logo} name={store.name} />
              </div>

              <p style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#111827' }}>{store.name}</p>
              <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#9ca3af' }}>{store.description || 'No description'}</p>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ── Main view ──────────────────────────────────────────────────────────────
  return (
    <div style={s.page}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600&display=swap');`}</style>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {currentUser?.role !== 'manager' && (
            <button
              onClick={() => { setSelectedStore(null); setStatusFilter('all'); }}
              style={{ display: 'flex', alignItems: 'center', gap: '4px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '8px 12px', fontSize: '13px', color: '#374151', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" }}
            >
              <IconChevronLeft /> Back
            </button>
          )}
          <div>
            <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 600, color: '#111827' }}>{selectedStore.name}</h1>
            <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#9ca3af' }}>{promotions.length} promotion{promotions.length !== 1 ? 's' : ''}</p>
          </div>
        </div>
        <button
          onClick={() => { setForm(defaultForm()); setShowCreateModal(true); }}
          style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#F1561D', color: '#fff', border: 'none', borderRadius: '8px', padding: '8px 16px', fontSize: '13px', fontWeight: 500, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" }}
        >
          <IconPlus /> New Promotion
        </button>
      </div>

      {/* Status filter tabs */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '24px', background: '#fff', borderRadius: '10px', padding: '4px', border: '1px solid #f3f4f6', width: 'fit-content' }}>
        {(['all', 'active', 'scheduled', 'expired', 'inactive'] as const).map(tab => (
          <button
            key={tab}
            onClick={() => setStatusFilter(tab)}
            style={{
              padding: '7px 14px', borderRadius: '7px', border: 'none', fontSize: '13px', fontWeight: 500,
              cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", transition: 'all 0.15s',
              background: statusFilter === tab ? '#F1561D' : 'transparent',
              color: statusFilter === tab ? '#fff' : '#6b7280',
            }}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}{' '}
            <span style={{ fontSize: '11px', opacity: 0.8 }}>({counts[tab]})</span>
          </button>
        ))}
      </div>

      {/* Content */}
      {loadingPromos ? (
        <p style={{ color: '#9ca3af', fontSize: '14px' }}>Loading promotions…</p>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '80px 0', color: '#9ca3af' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '12px' }}>
            <IconPackage />
          </div>
          <p style={{ margin: 0, fontSize: '15px', fontWeight: 500 }}>No promotions found</p>
          <p style={{ margin: '6px 0 0', fontSize: '13px' }}>
            {statusFilter !== 'all'
              ? `No ${statusFilter} promotions for this store`
              : 'Create your first promotion to get started'}
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {filtered.map(promo => {
            const sc = statusColor(promo.computedStatus);
            const scope = promo.productIds && promo.productIds.length > 0
              ? `${promo.productIds.length} product${promo.productIds.length !== 1 ? 's' : ''}`
              : 'All products';
            const isToggling = toggling === promo._id;
            const isDeleting = deleting === promo._id;

            return (
              <div
                key={promo._id}
                style={{ background: '#fff', borderRadius: '14px', border: '1px solid #f3f4f6', overflow: 'hidden', display: 'flex', alignItems: 'stretch' }}
              >
                {/* Status accent bar */}
                <div style={{ width: '4px', background: sc.dot, flexShrink: 0 }} />

                <div style={{ flex: 1, padding: '18px 20px', display: 'flex', gap: '16px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  {/* Icon */}
                  <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: '#fff5f2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <span style={{ color: '#F1561D' }}><IconTag /></span>
                  </div>

                  {/* Main info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '4px' }}>
                      <p style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: '#111827' }}>{promo.name}</p>
                      <span style={{ fontSize: '11px', fontWeight: 600, padding: '3px 9px', borderRadius: '20px', background: sc.bg, color: sc.text }}>
                        {promo.computedStatus}
                      </span>
                    </div>

                    {promo.description && (
                      <p style={{ margin: '0 0 6px', fontSize: '13px', color: '#6b7280' }}>{promo.description}</p>
                    )}

                    <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', fontSize: '12px', color: '#9ca3af' }}>
                      <span>
                        <strong style={{ color: '#F1561D', fontSize: '13px' }}>{formatDiscount(promo)}</strong>
                      </span>
                      <span>{formatDate(promo.startDate)} — {formatDate(promo.endDate)}</span>
                      <span>{scope}</span>
                    </div>

                    {/* Product chips */}
                    {promo.productIds && promo.productIds.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '8px' }}>
                        {promo.productIds.slice(0, 4).map((p: any) => (
                          <span key={typeof p === 'object' ? p._id : p} style={{ fontSize: '11px', background: '#f3f4f6', color: '#6b7280', padding: '2px 8px', borderRadius: '20px' }}>
                            {typeof p === 'object' ? p.name : p}
                          </span>
                        ))}
                        {promo.productIds.length > 4 && (
                          <span style={{ fontSize: '11px', background: '#f3f4f6', color: '#9ca3af', padding: '2px 8px', borderRadius: '20px' }}>
                            +{promo.productIds.length - 4} more
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div style={{ display: 'flex', gap: '8px', flexShrink: 0, alignItems: 'center' }}>
                    {/* Toggle */}
                    <button
                      onClick={() => handleToggle(promo._id)}
                      disabled={isToggling}
                      title={promo.isActive ? 'Pause promotion' : 'Resume promotion'}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '4px',
                        padding: '7px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: 500,
                        cursor: isToggling ? 'not-allowed' : 'pointer', fontFamily: "'DM Sans', sans-serif",
                        border: promo.isActive ? '1px solid #fde68a' : '1px solid #d1fae5',
                        background: promo.isActive ? '#fffbeb' : '#f0fdf4',
                        color: promo.isActive ? '#92400e' : '#065f46',
                        opacity: isToggling ? 0.6 : 1,
                        transition: 'all 0.15s',
                      }}
                    >
                      <IconToggle on={promo.isActive} />
                      {promo.isActive ? 'Pause' : 'Resume'}
                    </button>

                    {/* Edit */}
                    <button
                      onClick={() => openEdit(promo)}
                      style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '7px 12px', borderRadius: '8px', border: '1px solid #e5e7eb', background: '#fff', fontSize: '12px', fontWeight: 500, color: '#374151', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" }}
                    >
                      <IconEdit /> Edit
                    </button>

                    {/* Delete */}
                    <button
                      onClick={() => handleDelete(promo._id)}
                      disabled={isDeleting}
                      style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '7px 12px', borderRadius: '8px', border: '1px solid #fee2e2', background: '#fff5f5', fontSize: '12px', fontWeight: 500, color: '#dc2626', cursor: isDeleting ? 'not-allowed' : 'pointer', fontFamily: "'DM Sans', sans-serif", opacity: isDeleting ? 0.6 : 1 }}
                    >
                      <IconTrash /> {isDeleting ? 'Deleting…' : 'Delete'}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create modal */}
      {showCreateModal && (
        <PromotionModal
          title="New Promotion"
          form={form}
          setForm={setForm}
          products={products}
          onSubmit={handleCreate}
          onClose={() => { setShowCreateModal(false); setForm(defaultForm()); }}
          saving={saving}
          submitLabel="Create Promotion"
        />
      )}

      {/* Edit modal */}
      {showEditModal && editingPromo && (
        <PromotionModal
          title="Edit Promotion"
          form={form}
          setForm={setForm}
          products={products}
          onSubmit={handleEdit}
          onClose={() => { setShowEditModal(false); setEditingPromo(null); setForm(defaultForm()); }}
          saving={saving}
          submitLabel="Save Changes"
        />
      )}
    </div>
  );
};

export default Promotions;
